// src/components/VoucherEntryPopup.jsx
import React, { useEffect, useState, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  query,
  orderBy,
  doc,
  deleteDoc,
  runTransaction,
  limit,
  getDoc,
} from "firebase/firestore";
import ReactDOM from "react-dom";

/**
 * VoucherEntryPopup.jsx — patched version
 * - safer counter peek + atomic getNextVoucherNo
 * - cleaned save (remove empty rows, totals validation)
 * - enforce only one of debit/credit per row
 * - better focus handling for dynamic rows
 * - voucher list search without destructive filtering
 */

export default function VoucherEntryPopup({ onClose }) {
  const [voucherType, setVoucherType] = useState("Journal");
  const [voucherNo, setVoucherNo] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [parties, setParties] = useState([]);
  const [rows, setRows] = useState([
    { drcr: "Dr", partyId: "", partyName: "", debit: "", credit: "" },
  ]);
  const [narration, setNarration] = useState("");
  const [docId, setDocId] = useState(null);

  // Voucher list popup state
  const [voucherList, setVoucherList] = useState([]);
  const [showList, setShowList] = useState(false);
  const [voucherSearch, setVoucherSearch] = useState("");

  // Focus
  const [nextFocus, setNextFocus] = useState(null);
  const typeRef = useRef(null);
  const dateRef = useRef(null);
  const rowRefs = useRef([]);

  // rowRefs initialize
  useEffect(() => {
    rows.forEach((_, i) => {
      if (!rowRefs.current[i]) {
        rowRefs.current[i] = {
          drcr: React.createRef(),
          party: React.createRef(),
          debit: React.createRef(),
          credit: React.createRef(),
        };
      }
    });
    // trim excess refs if rows shrank
    rowRefs.current.length = rows.length;
  }, [rows]);

  // auto focus when nextFocus set (use timeout to ensure DOM ready)
  useEffect(() => {
    if (nextFocus !== null) {
      const { index, field } = nextFocus;
      setTimeout(() => {
        try {
          rowRefs.current[index]?.[field]?.current?.focus?.();
        } catch (e) {
          // ignore
        }
        setNextFocus(null);
      }, 40);
    }
  }, [rows, nextFocus]);

  const focusRow = (rowIndex, field = "drcr") => {
    setNextFocus({ index: rowIndex, field });
  };

  // safe peek of current counter without incrementing
  const peekCounter = async () => {
    try {
      const ref = doc(db, "counters", "voucher");
      const snap = await getDoc(ref);
      if (snap && snap.exists()) return Number(snap.data().lastNo || 1000);
      return 1000;
    } catch (e) {
      console.warn("peekCounter failed", e);
      return 1000;
    }
  };

  // atomic next voucher number
  const getNextVoucherNo = async () => {
    const counterRef = doc(db, "counters", "voucher");
    return await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(counterRef);
      if (!snap.exists()) {
        // initialize counter to 1001 and return 1001
        transaction.set(counterRef, { lastNo: 1001 });
        return 1001;
      }
      const current = Number(snap.data().lastNo || 1000);
      const newNo = current + 1;
      transaction.update(counterRef, { lastNo: newNo });
      return newNo;
    });
  };

  // initial voucher no preview
  useEffect(() => {
    let mounted = true;
    const initVoucherNo = async () => {
      const last = await peekCounter();
      if (mounted) setVoucherNo(Number(last) + 1);
    };
    initVoucherNo();
    return () => { mounted = false; };
  }, []);

  // load parties
  useEffect(() => {
    let cancelled = false;
    const fetchParties = async () => {
      try {
        const snap = await getDocs(collection(db, "parties"));
        if (cancelled) return;
        setParties(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.warn("fetch parties failed", e);
      }
    };
    fetchParties();
    return () => { cancelled = true; };
  }, []);

  // set initial focus on voucher type
  useEffect(() => {
    setTimeout(() => { try { typeRef.current?.focus?.(); } catch (e) {} }, 80);
  }, []);

  // totals
  const totals = rows.reduce(
    (acc, r) => {
      acc.debit += Number(r.debit || 0);
      acc.credit += Number(r.credit || 0);
      return acc;
    },
    { debit: 0, credit: 0 }
  );

  // ensure only one of debit/credit per row and keep strings for controlled inputs
  const handleRowChange = (i, field, value) => {
    setRows((prev) => {
      const copy = prev.map((r) => ({ ...r }));
      copy[i][field] = value;
      if (field === "debit") {
        // if user entered a non-empty non-zero debit, clear credit
        if (value !== "" && Number(value) !== 0) {
          copy[i].credit = "";
          copy[i].drcr = "Dr";
        }
      } else if (field === "credit") {
        if (value !== "" && Number(value) !== 0) {
          copy[i].debit = "";
          copy[i].drcr = "Cr";
        }
      }
      // if partyName selected update partyId where possible (handled by select change)
      return copy;
    });
  };

  const addRow = () => {
    setRows((prev) => {
      const nextIdx = prev.length;
      const next = [
        ...prev,
        { drcr: "Dr", partyId: "", partyName: "", debit: "", credit: "" },
      ];
      // focus after DOM updates — set nextFocus with slight delay
      setTimeout(() => setNextFocus({ index: nextIdx, field: "drcr" }), 50);
      return next;
    });
  };

  // Save
  const handleSave = async () => {
    try {
      // clean rows: remove fully empty rows (no partyName and no amounts)
      const cleaned = (rows || []).filter((r) => {
        const hasParty = (r.partyName || "").toString().trim() !== "";
        const hasAmt =
          (r.debit !== "" && Number(r.debit) !== 0) ||
          (r.credit !== "" && Number(r.credit) !== 0);
        return hasParty || hasAmt;
      });

      if (cleaned.length === 0) {
        alert("Enter at least one ledger row.");
        return;
      }

      // compute totals from cleaned rows (numbers)
      const debitTotal = cleaned.reduce(
        (s, r) => s + (parseFloat(r.debit || 0) || 0),
        0
      );
      const creditTotal = cleaned.reduce(
        (s, r) => s + (parseFloat(r.credit || 0) || 0),
        0
      );

      const roundTo2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
      if (roundTo2(debitTotal) !== roundTo2(creditTotal)) {
        alert("⚠️ Debit and Credit totals must be equal.");
        return;
      }

      const nextNo = await getNextVoucherNo();

      const payload = {
        voucherType,
        voucherNo: nextNo,
        date,
        rows: cleaned,
        narration,
        createdAt: new Date(),
        totals: { debit: debitTotal, credit: creditTotal },
      };

      const ref = await addDoc(collection(db, "vouchers"), payload);
      setDocId(ref.id);
      alert(`✅ Voucher Saved (No: ${nextNo})`);
      // reset UI for new voucher
      handleNew();
      // set preview to next expected number (read counter)
      const last = await peekCounter();
      setVoucherNo(Number(last) + 1);
    } catch (err) {
      console.error("Save error:", err);
      alert("Save failed: " + (err.message || err));
    }
  };

  // Find
  const handleFind = async () => {
    try {
      const snap = await getDocs(
        query(collection(db, "vouchers"), orderBy("voucherNo", "desc"))
      );
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setVoucherList(list);
      setShowList(true);
    } catch (err) {
      console.error("Find error:", err);
      alert("Failed to load vouchers");
    }
  };

  // Load selected voucher
  const handleLoadVoucher = (v) => {
    setDocId(v.id);
    setVoucherType(v.voucherType || "Journal");
    setVoucherNo(v.voucherNo || 0);
    setDate(v.date || new Date().toISOString().slice(0, 10));
    setRows(v.rows && v.rows.length ? v.rows : [{ drcr: "Dr", partyId: "", partyName: "", debit: "", credit: "" }]);
    setNarration(v.narration || "");
    setShowList(false);
    // focus first row drcr
    setTimeout(() => setNextFocus({ index: 0, field: "drcr" }), 60);
  };

  // Delete
  const handleDelete = async () => {
    if (!docId) return alert("❌ No voucher loaded");
    try {
      await deleteDoc(doc(db, "vouchers", docId));
      alert("🗑 Deleted");
      handleNew();
      const last = await peekCounter();
      setVoucherNo(last + 1);
    } catch (err) {
      console.error("Delete error:", err);
      alert("Delete failed: " + (err.message || err));
    }
  };

  // New
  const handleNew = () => {
    setVoucherType("Journal");
    setDate(new Date().toISOString().slice(0, 10));
    setRows([{ drcr: "Dr", partyId: "", partyName: "", debit: "", credit: "" }]);
    setNarration("");
    setDocId(null);
    // focus type
    setTimeout(() => { try { typeRef.current?.focus?.(); } catch (e) {} }, 40);
  };

  // filtered voucher list based on voucherSearch
  const filteredVoucherList = voucherList.filter((v) => {
    if (!voucherSearch) return true;
    const term = voucherSearch.toString().toLowerCase();
    const matchInRows = (v.rows || []).some((r) =>
      (r.partyName || "").toString().toLowerCase().includes(term)
    );
    const matchNarration = (v.narration || "").toString().toLowerCase().includes(term);
    const matchNo = (v.voucherNo || "").toString().includes(term);
    return matchInRows || matchNarration || matchNo;
  });

  // small helper to safely set party selection on change
  const handlePartySelect = (i, partyName) => {
    const selected = parties.find((p) => p.partyName === partyName);
    setRows((prev) => {
      const copy = prev.map((r) => ({ ...r }));
      copy[i].partyId = selected?.id || "";
      copy[i].partyName = selected?.partyName || partyName || "";
      return copy;
    });
  };

  // render popup
  const popupContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gray-300 w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center bg-gradient-to-r from-blue-700 to-blue-500 text-white px-6 py-1 rounded-t-2xl">
          <h2 className="text-lg font-semibold">📑 Voucher Entry</h2>
          <button
            onClick={onClose}
            className="text-red-300 hover:text-red-500 text-xl font-bold"
          >
            ✕
          </button>
        </div>

        {/* Voucher Info */}
        <div className="p-4 grid grid-cols-3 gap-4 border-b">
          <div>
            <label className="block text-sm font-medium">Voucher Type</label>
            <select
              ref={typeRef}
              value={voucherType}
              onChange={(e) => setVoucherType(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && dateRef.current?.focus()}
              className="mt-1 border rounded px-3 py-1 w-full"
            >
              <option>Journal</option>
              <option>Payment</option>
              <option>Receipt</option>
              <option>Contra</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium">Voucher No</label>
            <input
              type="number"
              value={voucherNo}
              readOnly
              className="mt-1 border rounded px-3 py-1 w-full bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Date</label>
            <input
              ref={dateRef}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && focusRow(0, "drcr")}
              className="mt-1 border rounded px-3 py-1 w-full"
            />
          </div>
        </div>

        {/* Ledger Table */}
        <div className="flex-1 overflow-auto p-4">
          <table className="w-full border text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2">Dr/Cr</th>
                <th className="border p-2">Party</th>
                <th className="border p-2">Debit</th>
                <th className="border p-2">Credit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="border p-2">
                    <select
                      ref={rowRefs.current[i]?.drcr}
                      value={r.drcr}
                      onChange={(e) => {
                        handleRowChange(i, "drcr", e.target.value);
                        // focus party after change
                        setTimeout(() => focusRow(i, "party"), 20);
                      }}
                      onKeyDown={(e) => e.key === "Enter" && focusRow(i, "party")}
                      className="border rounded px-2 py-1"
                    >
                      <option>Dr</option>
                      <option>Cr</option>
                    </select>
                  </td>
                  <td className="border p-2">
                    <select
                      ref={rowRefs.current[i]?.party}
                      value={r.partyName}
                      onChange={(e) => {
                        handlePartySelect(i, e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const current = rows[i];
                          if (current.drcr === "Dr") focusRow(i, "debit");
                          else focusRow(i, "credit");
                        }
                      }}
                      className="w-full border rounded px-2 py-1"
                    >
                      <option value="">-- Select Party --</option>
                      {parties.map((p) => (
                        <option key={p.id} value={p.partyName}>
                          {p.partyName}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="border p-2">
                    <input
                      ref={rowRefs.current[i]?.debit}
                      type="number"
                      min="0"
                      value={r.debit}
                      onChange={(e) => handleRowChange(i, "debit", e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (i === rows.length - 1) addRow();
                          else focusRow(i + 1, "drcr");
                        }
                      }}
                      className="w-full border rounded px-2 py-1 text-right"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      ref={rowRefs.current[i]?.credit}
                      type="number"
                      min="0"
                      value={r.credit}
                      onChange={(e) => handleRowChange(i, "credit", e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (i === rows.length - 1) addRow();
                          else focusRow(i + 1, "drcr");
                        }
                      }}
                      className="w-full border rounded px-2 py-1 text-right"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td colSpan={2} className="text-right p-2">Totals</td>
                <td className="border p-2 text-right">{totals.debit.toFixed(2)}</td>
                <td className="border p-2 text-right">{totals.credit.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Narration */}
        <div className="p-4 border-t">
          <label className="block text-sm font-medium mb-1">Narration</label>
          <textarea
            value={narration}
            onChange={(e) => setNarration(e.target.value)}
            className="w-full bg-gray-200 border rounded px-3 py-2"
            rows="2"
          />
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button onClick={handleSave} className="px-4 py-2 bg-gray-400 hover:bg-green-700 text-white rounded-lg">💾 Save</button>
          <button onClick={handleFind} className="px-4 py-2 bg-gray-400 hover:bg-blue-700 text-white rounded-lg">🔍 Find</button>
          <button onClick={handleDelete} className="px-4 py-2 bg-gray-400 hover:bg-red-700 text-white rounded-lg">🗑 Delete</button>
          <button onClick={handleNew} className="px-4 py-2 bg-gray-400 hover:bg-gray-700 text-white rounded-lg">➕ New</button>
        </div>

        {/* Voucher List Popup */}
        {showList && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div
              className="bg-gray-200 w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center bg-gradient-to-r from-blue-700 to-blue-500 text-white px-6 py-3 rounded-t-2xl">
                <h3 className="text-lg font-semibold">📑 Select Voucher</h3>
                <button
                  onClick={() => setShowList(false)}
                  className="text-red-300 hover:text-red-500 text-xl font-bold"
                >
                  ✕
                </button>
              </div>

              {/* Search */}
              <div className="p-4 border-b">
                <input
                  type="text"
                  placeholder="🔍 Search by party name / voucher no / narration..."
                  className="w-full border rounded-lg px-4 py-2 text-sm shadow-sm focus:ring focus:ring-blue-200"
                  value={voucherSearch}
                  onChange={(e) => setVoucherSearch(e.target.value)}
                />
              </div>

              {/* Table */}
              <div className="flex-1 overflow-auto p-4">
                <table className="w-full border text-sm rounded-lg overflow-hidden">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="border p-2">Voucher No</th>
                      <th className="border p-2">Type</th>
                      <th className="border p-2">Date</th>
                      <th className="border p-2">Parties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVoucherList.length > 0 ? (
                      filteredVoucherList.map((v) => (
                        <tr
                          key={v.id}
                          className="even:bg-gray-50 hover:bg-blue-50 cursor-pointer transition"
                          onDoubleClick={() => handleLoadVoucher(v)} // double click open
                        >
                          <td className="border p-2 text-center">{v.voucherNo}</td>
                          <td className="border p-2 text-center">{v.voucherType}</td>
                          <td className="border p-2 text-center">{v.date}</td>
                          <td className="border p-2 text-xs">
                            {(v.rows || []).map((r) => r.partyName).filter(Boolean).join(", ")}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="text-center text-gray-500 italic p-4"
                        >
                          No vouchers found…
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(popupContent, document.body);
}
